/**
 * Primary lender fee resolution + internal OB validation map.
 *
 * Architecture (post fees-in diagnostic):
 *  - Optimal Blue stays the pricing/eligibility engine and our compensation
 *    source. Its Lender Fees endpoint returns NO itemized rows on these DSCR
 *    products, so it is never the source of a borrower-facing fee line.
 *  - What the live diagnostic DID establish: for lenders that support
 *    feesIn=Yes, the price adjustment between feesIn=Yes and feesIn=No is
 *    almost exactly that investor's main Underwriting / Admin / Lender Fee.
 *  - So the headline fee column shows ONE fee only: that investor's primary
 *    origination/underwriting charge, taken from our own vetted schedule, and
 *    labelled with the lender's own fee name.
 *  - Every other schedule charge (entity review, tax service, flood, doc prep,
 *    funding, CDA, MERS, fraud/UDN, state attorney, etc.) moves to a secondary
 *    "other estimated costs" section and is never claimed to be in the APR.
 *
 * Nothing here computes an APR, and nothing here adds the primary fee into
 * points, credit, or a combined cash-to-close figure.
 */

import type { AppliedFee, LenderFeeEstimate } from "./lender-fee-rules";

/** Dollar variance at the reference scenario that still counts as a match. */
export const PRIMARY_FEE_TOLERANCE = 15;

/** Loan amount of the standard scenario the OB observations were taken on. */
export const OB_REFERENCE_LOAN_AMOUNT = 375_000;

export type PrimaryFeeMatchStatus =
  /** feesIn=Yes supported and our schedule amount matches the OB adjustment. */
  | "validated"
  /** feesIn=Yes supported but the observed adjustment differs beyond tolerance. */
  | "variance"
  /** Lender does not return on feesIn=Yes — fee is NOT reflected in pricing. */
  | "fees_in_unsupported"
  /** No OB observation on record for this investor yet. */
  | "not_validated";

export type PrimaryFeeValidation = {
  lenderKey: string;
  /** Fee rule ids in our schedule that can serve as the primary charge. */
  primaryFeeIds: string[];
  /** Our schedule amount at the reference scenario. */
  scheduleAmount: number | null;
  /** Observed OB feesIn price-adjustment equivalent at the reference scenario. */
  obReferenceAmount: number | null;
  feesInSupported: boolean;
  /**
   * True when the amount is confirmed against the lender's own maintained fee
   * schedule in this project even though no feesIn=Yes price observation is
   * available. Only such amounts may be used as an external (outside-price)
   * finance charge in the APR engine. Never set this without a source schedule.
   */
  manualScheduleVerified?: boolean;
  /** Date the observation was taken, for the audit trail. */
  observedOn: string | null;
  note?: string;
};

/**
 * INTERNAL audit map — investor keys never reach the browser as names, only
 * the derived match status and the borrower-safe fee label/amount do.
 * Observations are from the live read-only diagnostic on the standard
 * $375,000 / 75% LTV / 740 FICO / 1.20 DSCR FL purchase scenario.
 */
export const PRIMARY_FEE_VALIDATION: PrimaryFeeValidation[] = [
  {
    lenderKey: "arc_home",
    primaryFeeIds: ["arc.uw"],
    scheduleAmount: 1645,
    obReferenceAmount: 1646.25,
    feesInSupported: true,
    observedOn: "2026-08-25",
  },
  {
    lenderKey: "bluepoint",
    primaryFeeIds: ["bluepoint.lender_fee"],
    scheduleAmount: 2195,
    obReferenceAmount: 2193.75,
    feesInSupported: true,
    observedOn: "2026-08-25",
  },
  {
    lenderKey: "champions",
    primaryFeeIds: ["champions.uw"],
    scheduleAmount: 1995,
    obReferenceAmount: 1995,
    feesInSupported: true,
    observedOn: "2026-08-25",
  },
  {
    lenderKey: "clearedge",
    primaryFeeIds: ["clearedge.uw_high", "clearedge.uw_low"],
    scheduleAmount: 2100,
    obReferenceAmount: 2100,
    feesInSupported: true,
    observedOn: "2026-08-25",
  },
  {
    lenderKey: "foundation",
    primaryFeeIds: ["foundation.admin"],
    scheduleAmount: 1950,
    obReferenceAmount: null,
    feesInSupported: true,
    manualScheduleVerified: true,
    observedOn: null,
    note: "Administration/underwriting fee raised to $1,950 by the 'Doing Business With Foundation Mortgage' wholesale sheet rev. 9.8 (08 Sep 2026). The earlier $1,695 feesIn=Yes price observation (25 Aug 2026) predates that sheet, so no current OB reference amount is claimed until a new observation is taken.",
  },
  {
    lenderKey: "sun_west",
    primaryFeeIds: ["sunwest.uw"],
    scheduleAmount: 1299,
    obReferenceAmount: 1297.5,
    feesInSupported: true,
    observedOn: "2026-08-25",
  },
  {
    lenderKey: "deephaven",
    primaryFeeIds: ["deephaven.uw"],
    scheduleAmount: 1945,
    obReferenceAmount: null,
    feesInSupported: false,
    manualScheduleVerified: true,
    observedOn: "2026-08-25",
    note: "Returns no eligible products with feesIn=Yes; priced on the feesIn=No fallback. The $1,945 underwriting fee therefore sits OUTSIDE price and comes from the maintained Deephaven schedule (deephaven.uw).",
  },
  {
    lenderKey: "orion",
    primaryFeeIds: ["orion.uw"],
    scheduleAmount: 1695,
    obReferenceAmount: null,
    feesInSupported: false,
    // No feesIn=Yes price observation is on record for this investor yet, so
    // the fee is shown as an estimate outside pricing and is deliberately NOT
    // treated as a verified APR finance charge.
    manualScheduleVerified: false,
    observedOn: null,
    note: "Amount from the Orion Lending COIN DSCR fee schedule dated 04/07/2026. Awaiting a live feesIn=Yes observation before it may be used as an APR finance charge.",
  },
  {
    lenderKey: "new_wave",
    primaryFeeIds: ["newwave.lender_fee"],
    scheduleAmount: 1450,
    obReferenceAmount: null,
    feesInSupported: false,
    manualScheduleVerified: true,
    observedOn: "2026-08-25",
    note: "Returns no eligible products with feesIn=Yes; priced on the feesIn=No fallback. The $1,450 all-in lender fee therefore sits OUTSIDE price and comes from the maintained New Wave schedule (newwave.lender_fee).",
  },
  {
    lenderKey: "logan",
    primaryFeeIds: ["logan.admin"],
    scheduleAmount: 1945,
    obReferenceAmount: null,
    feesInSupported: false,
    // No live fees-in observation yet: shown as an estimate outside pricing,
    // not used as a verified APR finance charge until observed.
    manualScheduleVerified: false,
    observedOn: null,
    note: "Admin Fee (All In) $1,945 from the Logan Finance broker quick reference rev. 09/21/2026. Awaiting a live fees-in observation before APR use.",
  },
];

export function validationFor(lenderKey: string | null | undefined): PrimaryFeeValidation | null {
  if (!lenderKey) return null;
  return PRIMARY_FEE_VALIDATION.find((v) => v.lenderKey === lenderKey) ?? null;
}

/** Borrower-facing primary lender fee attached to a published quote. */
export type PrimaryLenderFee = {
  /** Fee rule id from our schedule, e.g. "arc.uw". Used to match central config. */
  feeId: string;
  /** Wholesale lender key, internal only — never rendered. */
  lenderKey: string | null;
  /** The lender's own fee name from our source schedule. Never invented. */
  label: string;
  amount: number | null;

  /** Which fee basis the quote was actually priced on. */
  feesInBasis: "Yes" | "No" | null;
  /** True only when feesIn=Yes AND the amount is validated within tolerance. */
  reflectedInPricing: boolean;
  /** Short consumer sublabel rendered under the amount. */
  sublabel: string;
  matchStatus: PrimaryFeeMatchStatus;
  /** Reference-scenario variance, for staff audit only. */
  varianceDollars: number | null;
  obReferenceAmount: number | null;
  /** True when staff should review before quoting in writing. */
  reviewFlag: boolean;
  /**
   * True when this amount is defensible as an APR input: either validated
   * against the live feesIn=Yes price observation, or confirmed against the
   * lender's maintained fee schedule for a lender that only prices feesIn=No.
   */
  verifiedForApr: boolean;
};

export const PRIMARY_FEE_REFLECTED_SUBLABEL = "Primary lender fee reflected in pricing";
export const PRIMARY_FEE_SEPARATE_SUBLABEL = "Estimated lender fee — not reflected in pricing";

/**
 * APR copy. Deliberately does NOT claim all lender fees are included — the
 * diagnostic did not establish that.
 */
export const APR_DISCLOSURE_NOTE =
  "APR reflects applicable borrower-paid finance charges and pricing credits for this quote. Depending on the option, the APR shown is either the APR returned with that quote or our own audited calculation, used where verified borrower-paid finance charges outside the quoted price apply. Additional lender and third-party costs shown below may not be included in the APR.";

export const APR_FALLBACK_NOTE =
  "This option was priced on the lender's alternate fee basis. Where that lender fee is a verified borrower-paid charge outside the quoted price, the APR shown is our audited calculation including it; otherwise the APR returned with the quote is shown and the estimated lender fee below is separate from it.";

/** Original disclosure for the secondary cost section. */
export const OTHER_COSTS_DISCLOSURE =
  "About estimated lender and closing costs: The primary lender fee shown with each quote is based on the lender's published fee schedule and is used as part of that lender's pricing. Other lender or closing costs may apply depending on the property, borrower, state, vesting, product, and transaction. These estimates do not represent all costs to close and may not be included in the APR shown. Appraisal, title/settlement, recording charges, taxes, insurance, escrows/prepaids, and other third-party charges may also apply. Final costs are determined by the lender and closing providers and will appear on the applicable loan and closing disclosures.";

/**
 * Short heading used above the standing cost-education block near results.
 */
export const OTHER_COSTS_HEADING = "Other Estimated Lender & Closing Costs";

/** Pull the primary fee line out of the evaluated schedule, if present. */
export function findPrimaryFeeLine(
  est: LenderFeeEstimate | null | undefined,
  validation: PrimaryFeeValidation | null,
): AppliedFee | null {
  if (!est || !validation) return null;
  for (const id of validation.primaryFeeIds) {
    const hit = est.fixedFees.find((f) => f.id === id);
    if (hit) return hit;
  }
  return null;
}

/** Every other schedule charge — these belong in the secondary section only. */
export function otherFixedFees(
  est: LenderFeeEstimate | null | undefined,
  primary: AppliedFee | null,
): AppliedFee[] {
  if (!est) return [];
  return est.fixedFees.filter((f) => f.id !== primary?.id);
}

/**
 * Resolve the borrower-facing primary lender fee for one quote.
 *
 * Rules:
 *  - Amount always comes from OUR schedule, never converted from a price
 *    adjustment.
 *  - "Reflected in pricing" is claimed ONLY when the quote was priced on
 *    feesIn=Yes and the reference-scenario variance is inside tolerance.
 *  - Anything else is presented as a separate estimated fee and flagged.
 */
export function resolvePrimaryLenderFee(
  est: LenderFeeEstimate | null | undefined,
  feesInBasis: "Yes" | "No" | null,
): PrimaryLenderFee | null {
  const validation = validationFor(est?.lenderKey ?? null);
  const line = findPrimaryFeeLine(est, validation);
  if (!line) return null;

  const variance =
    validation?.obReferenceAmount != null && validation.scheduleAmount != null
      ? Math.round((validation.scheduleAmount - validation.obReferenceAmount) * 100) / 100
      : null;

  let matchStatus: PrimaryFeeMatchStatus;
  if (!validation || validation.obReferenceAmount == null) {
    matchStatus = validation && !validation.feesInSupported ? "fees_in_unsupported" : "not_validated";
  } else if (variance != null && Math.abs(variance) <= PRIMARY_FEE_TOLERANCE) {
    matchStatus = "validated";
  } else {
    matchStatus = "variance";
  }

  const reflectedInPricing = feesInBasis === "Yes" && matchStatus === "validated";

  // APR eligibility. Either the live feesIn=Yes observation validated the
  // amount, or the lender only prices feesIn=No and our maintained schedule
  // supplies the amount. Nothing else qualifies — an unverified amount must
  // fall back to the pricing engine's APR rather than be treated as a charge.
  const verifiedForApr =
    line.amount != null &&
    (matchStatus === "validated" ||
      (matchStatus === "fees_in_unsupported" &&
        validation?.manualScheduleVerified === true &&
        validation.scheduleAmount === line.amount));

  return {
    feeId: line.id,
    lenderKey: est?.lenderKey ?? null,
    label: line.label,
    amount: line.amount,

    feesInBasis,
    reflectedInPricing,
    sublabel: reflectedInPricing ? PRIMARY_FEE_REFLECTED_SUBLABEL : PRIMARY_FEE_SEPARATE_SUBLABEL,
    matchStatus,
    varianceDollars: variance,
    obReferenceAmount: validation?.obReferenceAmount ?? null,
    reviewFlag: !reflectedInPricing,
    verifiedForApr,
  };
}
